import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './DashboardList.css';
import { API_BASE } from '../constants/api';
import { fetchListing } from '../utils/thingsboardEdition';
import { useDropdownClose } from '../hooks/useDropdownClose';
import { useFocusTrap } from '../hooks/useFocusTrap';
import Pagination from './Pagination';
import SearchWithSuggestions from './SearchWithSuggestions';
import { useAuth } from '../context/AuthContext';
import { Hinzufügen, Sortieren, Export, Abbrechen, Öffnen, Löschen } from '../icons';
import { generateSuggestions } from '../utils/suggestions';
import { formatDateDE } from '../utils/formatDate';

const PAGE_SIZE = 10;

function DashboardList() {
  const [dashboardCount, setDashboardCount] = useState(null);
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef(null);

  const [sortOption, setSortOption] = useState("created");
  const [showSortOptions, setShowSortOptions] = useState(false);

  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  /* Löschen */
  const [dashboardToDelete, setDashboardToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const deleteConfirmRef = useRef(null);

  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState('success');

  const { authToken: token } = useAuth();
  const navigate = useNavigate();

  const closeSortDropdown = useCallback(() => setShowSortOptions(false), []);
  useDropdownClose(showSortOptions, closeSortDropdown);
  useFocusTrap(showDeleteConfirm, deleteConfirmRef);

  function showStatus(message, type = 'success') {
    setStatusType(type);
    setStatusMessage(message);
    setTimeout(() => setStatusMessage(""), 4000);
  }

  async function deleteDashboard() {
    if (!dashboardToDelete) return;
    try {
      setDeleting(true);
      const res = await fetch(
        `${API_BASE}/dashboard/${dashboardToDelete.id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || errData?.message || `Dashboard konnte nicht gelöscht werden (HTTP ${res.status})`);
      }
      setDashboards(prev => prev.filter(d => d.id !== dashboardToDelete.id));
      setDashboardCount(c => Math.max(0, c - 1));
      showStatus(`Dashboard \u201E${dashboardToDelete.name}\u201C wurde erfolgreich gelöscht.`);
    } catch (e) {
      console.error(e);
      showStatus(`Dashboard konnte nicht gelöscht werden: ${e.message || 'Netzwerkfehler'}.`, 'error');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
      setDashboardToDelete(null);
    }
  }

  /* ===============================
     Dashboards laden (SERVERSEITIG)
  =============================== */
  useEffect(() => {
    async function loadDashboards() {
      try {
        setLoading(true);
        setError("");
        const sortMap = {
          name: { property: "name", order: "ASC" },
          created: { property: "createdTime", order: "DESC" },
        };
        const sort = sortMap[sortOption];
        const params = {
          pageSize: PAGE_SIZE,
          page,
          sortProperty: sort.property,
          sortOrder: sort.order,
          includeCustomers: true
        };
        if (searchTerm) {
          params.textSearch = searchTerm;
        }
        const res = await fetchListing('dashboards/all', params, token);
        if (!res.ok) {
          const errData = await res.json().catch(() => null);
          throw new Error(errData?.detail || errData?.message || `Fehler beim Laden der Dashboards (HTTP ${res.status})`);
        }
        const data = await res.json();
        const normalizedDashboards = (data.data || []).map(d => ({
          id: d.id.id,
          name: d.name || d.title,
          createdTime: d.createdTime,
          groups: (d.groups || [])
            .filter(g => g.name !== "All")
            .map(g => ({ id: g.id?.id ?? g.id, name: g.name })),
        }));
        setDashboards(normalizedDashboards);
        setDashboardCount(data.totalElements);
        setTotalPages(data.totalPages);
      } catch (err) {
        setError(err.message || "Fehler beim Laden der Dashboards. Bitte prüfen Sie Ihre Netzwerkverbindung.");
        setDashboards([]);
        setDashboardCount(0);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    }
    if (token) {
      loadDashboards();
    }
  }, [token, page, sortOption, searchTerm]);

  /* ===============================
     Such-Vorschläge
  =============================== */
  const suggestions = generateSuggestions(dashboards, searchTerm);

  /* ===============================
     Render
  =============================== */
  return (
    <div>
      {/* Toolbar */}
      <div className="dashboard-toolbar">
        <div className="dashboard-toolbar-left">
          <h1 className="dashboard-title">
            Alle Dashboards ({dashboardCount ?? "..."})
          </h1>
          <div className="sort-wrapper">
            <button
              className={`sort-button ${showSortOptions ? "active" : ""}`}
              onClick={() => setShowSortOptions(p => !p)}
              aria-expanded={showSortOptions}
              aria-haspopup="listbox"
              aria-label="Sortieroptionen anzeigen"
              title="Sortieroptionen anzeigen"
            >
              <span className="sort-icon"><Sortieren /></span> Sortieren
            </button>
            {showSortOptions && (
              <div className="sort-dropdown" role="listbox">
                {[{key: "name", label: "Name"}, {key: "created", label: "Hinzugefügt"}].map((opt) => (
                  <div
                    key={opt.key}
                    className={`sort-option ${sortOption === opt.key ? "active" : ""}`}
                    role="option"
                    aria-selected={sortOption === opt.key}
                    tabIndex={0}
                    onClick={() => { setSortOption(opt.key); setPage(0); setShowSortOptions(false); setTimeout(() => searchInputRef.current?.focus(), 0); }}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        setSortOption(opt.key); setPage(0); setShowSortOptions(false); setTimeout(() => searchInputRef.current?.focus(), 0);
                      }
                    }}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="dashboard-toolbar-right">
          <button
            className="export-button"
            aria-label="Neues Dashboard erstellen"
            title="Neues Dashboard erstellen"
            onClick={() => navigate('/dashboard/new')}
          >
            <div className="export-icon"><Hinzufügen /></div>
            <div>Neues Dashboard</div>
          </button>

          <button
            className="export-button"
            aria-label="Daten exportieren"
            title="Daten exportieren"
            disabled
          >
            <div className="export-icon"><Export /></div>
            <div>Daten exportieren</div>
          </button>

          {/* Suche */}
          <SearchWithSuggestions
            ref={searchInputRef}
            id="dashboard-search"
            label="Dashboard suchen"
            value={searchTerm}
            onChange={(val) => { setSearchTerm(val); setPage(0); }}
            suggestions={suggestions}
            suggestionsId="dashboard-suggestions"
          />
        </div>
      </div>

      {statusMessage && (
        <div className={`status-bar${statusType === 'error' ? ' status-bar--error' : ''}`} role="status" aria-live="polite">
          <p>{statusMessage}</p>
        </div>
      )}

      {/* Liste */}
      <div className="device-table-container" role="region" aria-label="Dashboardliste">
        {loading ? (
          <p>Dashboards werden geladen…</p>
        ) : error ? (
          <div role="alert" className="error-message">{error}</div>
        ) : (
          <>
            <div className="dashboard-list" role="list" aria-label="Dashboards">
              <div className="dashboard-row dashboard-header" role="row">
                <div>Dashboard-Name</div>
                <div>Gruppen</div>
                <div>Hinzugefügt</div>
                <div>Öffnen</div>
                <div>Löschen</div>
              </div>
              {(!dashboards || dashboards.length === 0) ? (
                <div className="no-items" style={{ padding: '12px 20px', color: '#474747' }}>Keine Dashboards gefunden</div>
              ) : dashboards.map(d => (
                <div className="dashboard-row dashboard-item clickable-row" key={d.id} role="listitem"
                  onClick={(e) => {
                    if (e.target.closest('button, .sort-wrapper')) return;
                    navigate(`/dashboard/view/${d.id}`, { state: { name: d.name } });
                  }}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && !e.target.closest('button, .sort-wrapper')) {
                      e.preventDefault();
                      navigate(`/dashboard/view/${d.id}`, { state: { name: d.name } });
                    }
                  }}
                  tabIndex={0}
                  aria-label={`Dashboard ${d.name} öffnen`}
                >
                  <div>{d.name}</div>
                  <div className="group-badges" aria-label="Gruppen">
                    {d.groups.length ? d.groups.map(g => (
                      <span
                        key={g.id ?? g.name}
                        className="group-badge"
                        title={`Gruppe: ${g.name}`}
                        role="button"
                        tabIndex={0}
                        style={{ cursor: 'pointer' }}
                        onClick={e => { e.stopPropagation(); navigate(`/dashboard-groups/${g.id}`, { state: { name: g.name } }); }}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); navigate(`/dashboard-groups/${g.id}`, { state: { name: g.name } }); } }}
                      >{g.name}</span>
                    )) : "–"}
                  </div>
                  <div>{formatDateDE(d.createdTime)}</div>
                  <div className="cell-right">
                    <button
                      className="icon-button open"
                      aria-label={`Dashboard ${d.name} öffnen`}
                      title={`Dashboard ${d.name} öffnen`}
                      tabIndex={0}
                      onClick={() => navigate(`/dashboard/view/${d.id}`, { state: { name: d.name } })}
                    >
                      <Öffnen />
                    </button>
                  </div>
                  <div className="cell-right">
                    <button
                      className="icon-button delete"
                      aria-label={`Dashboard ${d.name} löschen`}
                      title={`Dashboard ${d.name} löschen`}
                      tabIndex={0}
                      onClick={() => {
                        setDashboardToDelete({ id: d.id, name: d.name });
                        setShowDeleteConfirm(true);
                      }}
                    >
                      <Löschen />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {/* Pagination */}
            {dashboardCount > 0 && (
              <Pagination page={page} totalPages={totalPages} totalItems={dashboardCount} pageSize={PAGE_SIZE} onPageChange={setPage} label="Dashboards" />
            )}
          </>
        )}
      </div>

      {showDeleteConfirm && dashboardToDelete && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="delete-confirm-title">
          <div className="modal" ref={deleteConfirmRef}>
            <h2 id="delete-confirm-title">
              Sind Sie sicher, dass Sie das Dashboard <br/>
              <i>{dashboardToDelete.name}</i> löschen möchten?
            </h2>

            <p>
              Nach der Bestätigung wird das Dashboard und alle zugehörigen Daten <strong>unwiderruflich</strong> gelöscht.
            </p>

            <div className="modal-actions">
              <button
                className="modal-primary"
                onClick={deleteDashboard}
                disabled={deleting}
              >
                {deleting ? (
                    "Löschen…"
                  ) : (
                    <>
                      <Löschen />
                      Löschen bestätigen
                    </>
                  )}
              </button>

              <button
                className="modal-secondary"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDashboardToDelete(null);
                }}
                disabled={deleting}
              >
                <Abbrechen /> Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardList;
